import React, { useContext, useState, useEffect } from "react"
import { createStorefrontApiClient } from "@shopify/storefront-api-client"
import {
  GRAPHQL_PRODUCT_FIELDS,
  GRAPHQL_PRODUCT_VARIANTS,
  GRAPHQL_CART_FIELDS,
} from "../queries/shopify"
import { useLocalStorageV2 } from "../hooks/useLocalStorage"
import { debugLog, debugError, debugWarn } from "../helpers/debug"

const client = createStorefrontApiClient({
  storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL,
  apiVersion: process.env.GATSBY_SHOPIFY_API_VERSION || "2025-01",
  publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
})

const defaultValues = {
  cart: [],
  isOpen: false,
  loading: false,
  onOpen: () => {},
  onClose: () => {},
  addVariantToCart: () => {},
  updateLineItem: () => {},
  client,
  checkout: null,
}

const isBrowser = typeof window !== `undefined`
const LOCAL_STORAGE_KEY = `sftm_cart_id`

export const StoreContext = React.createContext(defaultValues)

export function useStoreContext() {
  const storeContext = useContext(StoreContext)
  return storeContext
}

async function fetchProductById(id) {
  if (!id) return null
  const query = `{
    product(id: "${id}") {
      ${GRAPHQL_PRODUCT_FIELDS}
      ${GRAPHQL_PRODUCT_VARIANTS}
    }
  }`
  return await client.request(query)
}

function useCurrentProduct(currentProduct) {
  const [product, set] = useState(currentProduct)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Handle response
    function handleResult(product) {
      set({
        ...currentProduct,
      })
    }

    // Fetch product by ID
    async function fetchProduct() {
      if (!currentProduct?.productId) return

      setLoading(true)
      setError(null)

      try {
        const response = await fetchProductById(currentProduct.productId)
        handleResult(response?.data?.product)
      } catch (err) {
        setError(err)
        console.error("Error fetching product:", err)
      } finally {
        setLoading(false)
      }
    }

    // Init
    fetchProduct()
  }, [currentProduct?.productId]) // Only re-run if productId changes

  return [product, set]
}

async function fetchCartById(id) {
  if (!id) return null

  const query = `
    {
      cart (
        id: "${id}"
      ) {
        ${GRAPHQL_CART_FIELDS}
      }
    }
  `
  const { data, errors } = await client.request(query)

  if (errors) {
    console.error("cart response error", errors)
    return null
  } else {
    return data
  }
}

async function createCart() {
  const operation = `mutation createCart {
    cartCreate {
      cart {
        ${GRAPHQL_CART_FIELDS}
      }
    }
  }`

  const { data, errors } = await client.request(operation)

  if (errors) {
    console.error("createCart error", errors)
    return null
  } else {
    return data.cartCreate.cart
  }
}

async function addLineItem(cartId = "", lines = []) {
  /* Line item example:
    const lineItemExample = {
      merchandiseId: variantId,
      quantity: 1,
      attributes: [
        key: "gift-wrap",
        value: "yes"
      ]
    }
  */
  const operation = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          ${GRAPHQL_CART_FIELDS}
        }
        userErrors {
          field
          message
        }
      }
    }
  `
  const { data, errors } = await client.request(operation, {
    variables: {
      lines: lines,
      cartId: cartId,
    },
  })

  debugLog('addLineItem API response', { data, errors, hasUserErrors: !!data?.cartLinesAdd?.userErrors?.length })

  if (errors) {
    debugError("addLineItem GraphQL errors", errors)
    return null
  }

  // Check for Shopify userErrors (validation errors)
  if (data?.cartLinesAdd?.userErrors?.length > 0) {
    debugError("addLineItem userErrors", data.cartLinesAdd.userErrors)
    return null
  }

  if (!data?.cartLinesAdd?.cart) {
    debugError("addLineItem returned no cart", data)
    return null
  }

  debugLog('addLineItem successful')
  return data.cartLinesAdd
}

async function updateCartLineItem(cartId = "", lines = []) {
  /**
   * Line item example:
   * const lineItemExample = {
   *  id: lineItemId,
   *  quantity: 0
   * }
   */

  const operation = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          ${GRAPHQL_CART_FIELDS}
        }
        userErrors {
          field
          message
        }
      }
    }
  `
  const { data, errors } = await client.request(operation, {
    variables: {
      cartId: cartId,
      lines: lines,
    },
  })

  if (errors) {
    console.error("cartLinesUpdate error", errors)
    return null
  } else {
    return data.cartLinesUpdate
  }
}

const StoreProvider = ({ product, children }) => {
  const localStorageCart = useLocalStorageV2(LOCAL_STORAGE_KEY, null)
  const [loading, setLoading] = useState(false)
  const [didJustAddToCart, setDidJustAddToCart] = useState(false)

  // Current product store
  const [currentProduct, setCurrentProduct] = useCurrentProduct(product)

  // -------------------------
  // V2 cart
  const cartInitialized = localStorageCart?.initialized
  const cartId = localStorageCart?.value
  const [cart, setCart] = useState(null)
  const [error, setError] = useState(null)

  debugLog('StoreProvider initialized', {
    cartInitialized,
    cartId,
    hasLocalStorageCart: !!localStorageCart,
    productId: product?.productId
  })

  useEffect(() => {
    debugLog('Cart initialization useEffect triggered', { 
      cartInitialized, 
      cartId, 
      hasCart: !!cart,
      loading 
    })

    // Handle response
    function handleExisitingCart(cart) {
      debugLog('Setting existing cart', { cartId: cart.id, lineItemsCount: cart.lines?.edges?.length || 0 })
      setCart(cart)
    }

    function handleNewCart(cart) {
      debugLog('Setting new cart', { cartId: cart.id })
      localStorageCart.setValue(cart.id)
      setCart(cart)
    }

    // Fetch product by ID
    async function updateCart() {
      debugLog('updateCart function called', { cartInitialized, hasCart: !!cart })
      
      if (cartInitialized && !cart) {
        if (cartId) {
          debugLog('Attempting to fetch existing cart', { cartId })
          setLoading(true)
          setError(null)

          try {
            const exisitingCart = await fetchCartById(cartId)
            if (exisitingCart) {
              debugLog('Successfully fetched existing cart')
              handleExisitingCart(exisitingCart.cart)
            } else {
              debugWarn('No existing cart found, creating new cart')
              throw new Error("No existing cart found")
            }
          } catch (err) {
            setError(err)
            debugError("Error fetching cart", err)
            // Try to create a new cart as fallback
            try {
              debugLog('Creating fallback cart after fetch failure')
              const newCart = await createCart()
              if (newCart) {
                handleNewCart(newCart)
              }
            } catch (createErr) {
              debugError("Error creating new cart after existing cart failed", createErr)
            }
          } finally {
            setLoading(false)
          }
        } else {
          debugLog('No cartId in localStorage, creating new cart')
          setLoading(true)
          try {
            const newCart = await createCart()
            if (newCart) {
              handleNewCart(newCart)
            }
          } catch (err) {
            setError(err)
            debugError("Error creating cart", err)
          } finally {
            setLoading(false)
          }
        }
      } else {
        debugLog('Cart initialization skipped', { 
          reason: cartInitialized ? 'cart already exists' : 'localStorage not initialized',
          cartInitialized,
          hasCart: !!cart
        })
      }
    }

    // Init
    updateCart()
  }, [cartId, cartInitialized]) // Removed cart from dependencies to prevent infinite loop

  const addVariantToCart = async (
    variantId,
    quantity,
    customAttributes,
    callback
  ) => {
    debugLog('addVariantToCart called', { 
      variantId, 
      quantity, 
      customAttributes,
      hasCart: !!cart,
      cartId: cart?.id 
    })
    
    setLoading(true)

    // Check if cart is ready
    if (!cart?.id) {
      debugError('Cannot add to cart: cart not initialized', { 
        cart, 
        cartInitialized, 
        cartId: localStorageCart?.value 
      })
      setLoading(false)
      return false
    }

    try {
      const lineItem = {
        merchandiseId: variantId,
        quantity: parseInt(quantity, 10),
        attributes: customAttributes,
      }

      debugLog('Adding line item to cart', { lineItem, cartId: cart.id })
      
      const updatedCart = await addLineItem(cart.id, [lineItem])
      if (updatedCart?.cart) {
        debugLog('Successfully added to cart', { 
          newLineItemsCount: updatedCart.cart.lines?.edges?.length || 0 
        })
        if (callback) callback(updatedCart.cart)
        setCart(updatedCart.cart)
        return true
      } else {
        debugError("Add to cart failed: no updated cart returned", { updatedCart, hasCart: !!updatedCart?.cart })
        return false
      }
    } catch (error) {
      debugError("Add to cart error", error)
      return false
    } finally {
      setLoading(false)
      setDidJustAddToCart(true)
      setTimeout(() => setDidJustAddToCart(false), 500)
    }
  }

  const updateLineItem = async (lineItemID, quantity) => {
    debugLog('updateLineItem called', { lineItemID, quantity, cartId: cart?.id })
    setLoading(true)

    if (!cart?.id) {
      debugError('Cannot update line item: cart not initialized')
      setLoading(false)
      return false
    }

    try {
      const updateCart = await updateCartLineItem(cart.id, [
        { id: lineItemID, quantity: parseInt(quantity, 10) },
      ])
      if (updateCart) {
        debugLog('Successfully updated line item', { 
          lineItemID, 
          newQuantity: quantity,
          newLineItemsCount: updateCart.cart.lines?.edges?.length || 0 
        })
        setCart(updateCart.cart)
        return true
      }
    } catch (error) {
      debugError("updateLineItem error", error)
      return false
    } finally {
      setLoading(false)
    }
  }

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addVariantToCart,
        updateLineItem,
        loading,
        didJustAddToCart,
        currentProduct,
        setCurrentProduct,
        // V2
        cart,
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}

export default StoreProvider
